/**
 * AdvertisingPage - cliente Profissional+ gere os seus anúncios no Marketplace MeSafa.
 */
import { useEffect, useState, useRef } from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Sparkles, Plus, Edit3, Trash2, Loader2, ImagePlus, ExternalLink, Megaphone, ShieldAlert, Crown } from "lucide-react";
import {
    Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
    Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { api, getApiErrorMessage } from "@/lib/api";
import { toast } from "sonner";
import { useAuth } from "@/lib/auth";
import StatusBadge from "@/components/shared/StatusBadge";
import EmptyState from "@/components/shared/EmptyState";
import { fmtDateTime } from "@/lib/date";
import { Link } from "react-router-dom";

const INDUSTRIES = [
    "Contabilidade & Finanças",
    "Tecnologia & Web",
    "Marketing & Publicidade",
    "Recursos Humanos",
    "Logística & Transporte",
    "Construção & Imobiliário",
    "Saúde & Bem-estar",
    "Educação & Formação",
    "Restauração",
    "Outro",
];

const PLANS_WITH_ADS = ["profissional", "empresarial", "agencia"];

function adStatusKey(s) {
    if (s === "em_revisao") return "pendente";
    if (s === "aprovado" || s === "ativo") return "aprovado";
    if (s === "rejeitado") return "rejeitado";
    if (s === "pausado") return "pausada";
    if (s === "expirado") return "concluida";
    return "rascunho";
}

export default function AdvertisingPage() {
    const { user } = useAuth();
    const [ads, setAds] = useState([]);
    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [saving, setSaving] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [form, setForm] = useState({
        title: "",
        headline: "",
        description: "",
        image_url: "",
        cta_label: "Saber mais",
        target_url: "",
        industry: "Tecnologia & Web",
        target_audience: "",
        budget_aoa: 30000,
        duration_days: 30,
    });

    const isEligible = user && (user.role === "admin" || (PLANS_WITH_ADS.includes(user.plan_id) && user.subscription_status === "ativa"));

    const load = () => {
        if (!isEligible) return;
        api.get("/ads/mine").then((r) => setAds(r.data));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
    useEffect(() => { load(); }, [isEligible]);

    const openNew = () => {
        setEditing(null);
        setForm({
            title: "",
            headline: "",
            description: "",
            image_url: "",
            cta_label: "Saber mais",
            target_url: "",
            industry: "Tecnologia & Web",
            target_audience: "",
            budget_aoa: 30000,
            duration_days: 30,
        });
        setOpen(true);
    };

    const openEdit = (a) => {
        setEditing(a);
        setForm({
            title: a.title || "",
            headline: a.headline || "",
            description: a.description || "",
            image_url: a.image_url || "",
            cta_label: a.cta_label || "Saber mais",
            target_url: a.target_url || "",
            industry: a.industry || "Tecnologia & Web",
            target_audience: a.target_audience || "",
            budget_aoa: a.budget_aoa || 30000,
            duration_days: a.duration_days || 30,
        });
        setOpen(true);
    };

    const remove = async (a) => {
        if (!window.confirm("Eliminar este anúncio?")) return;
        try {
            await api.delete(`/ads/${a.id}`);
            toast.success("Anúncio eliminado");
            load();
        } catch (e) { toast.error(getApiErrorMessage(e)); }
    };

    const generateImage = async () => {
        if (!form.headline && !form.title) {
            toast.error("Adicione título ou headline antes");
            return;
        }
        setGenerating(true);
        try {
            const { data } = await api.post("/ai/image", {
                prompt: `Professional B2B advertising image for: ${form.headline || form.title}. Industry: ${form.industry}. Modern, premium look.`,
                aspect: "16:9",
            });
            setForm((f) => ({ ...f, image_url: data.image_data_url }));
            toast.success("Imagem gerada com IA!");
        } catch (e) { toast.error(getApiErrorMessage(e)); }
        finally { setGenerating(false); }
    };

    const save = async () => {
        if (!form.title || !form.headline || !form.target_url) {
            toast.error("Preencha título, headline e link.");
            return;
        }
        setSaving(true);
        try {
            if (editing) {
                await api.put(`/ads/${editing.id}`, form);
                toast.success("Anúncio atualizado e enviado para revisão");
            } else {
                await api.post("/ads", form);
                toast.success("Anúncio submetido para revisão!");
            }
            setOpen(false);
            load();
        } catch (e) { toast.error(getApiErrorMessage(e)); }
        finally { setSaving(false); }
    };

    if (!isEligible) {
        return (
            <div className="space-y-6" data-testid="advertising-page-locked">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight">
                        Publicidade no Marketplace MeSafa
                    </h1>
                    <p className="text-muted-foreground">Venda os seus serviços a outras empresas que usam a plataforma.</p>
                </div>
                <Card className="glass-card p-8 rounded-2xl text-center border-[#6B21D9]/30">
                    <div className="h-14 w-14 mx-auto rounded-2xl btn-grad-purple grid place-items-center text-white mb-4">
                        <Crown className="h-7 w-7" />
                    </div>
                    <h2 className="font-display text-2xl font-semibold">Funcionalidade Profissional+</h2>
                    <p className="mt-2 text-muted-foreground max-w-lg mx-auto">
                        O Marketplace MeSafa está disponível para subscritores dos planos
                        <strong> Profissional</strong>, <strong>Empresarial</strong> e <strong>Agência Personalizado</strong>.
                        Anuncie os seus serviços a centenas de PMEs angolanas.
                    </p>
                    <Button asChild className="mt-5 btn-grad-purple text-white" data-testid="advertising-upgrade-cta">
                        <Link to="/subscricao">Fazer upgrade</Link>
                    </Button>
                </Card>
            </div>
        );
    }

    return (
        <div className="space-y-6" data-testid="advertising-page">
            <div className="flex flex-col gap-2 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="font-display text-2xl sm:text-3xl font-semibold tracking-tight flex items-center gap-2">
                        <Megaphone className="h-7 w-7 text-[#A78BFA]" /> Marketplace MeSafa
                    </h1>
                    <p className="text-muted-foreground">Anuncie os seus serviços B2B a outras empresas na plataforma.</p>
                </div>
                <Button onClick={openNew} className="btn-grad-purple text-white" data-testid="advertising-new-button">
                    <Plus className="h-4 w-4 mr-2" /> Novo anúncio
                </Button>
            </div>

            <Card className="glass-card p-4 rounded-2xl border-[#6B21D9]/20 flex items-start gap-3">
                <ShieldAlert className="h-5 w-5 text-[#FBBF24] shrink-0 mt-0.5" />
                <div className="text-sm">
                    Todos os anúncios passam por <strong>revisão pela equipa MeSafa</strong> antes de serem publicados.
                    O processo demora até 24h. Anúncios válidos são exibidos a milhares de PMEs em Angola.
                </div>
            </Card>

            {ads.length === 0 ? (
                <EmptyState
                    icon={Megaphone}
                    title="Sem anúncios"
                    description="Crie o seu primeiro anúncio e venda os seus serviços a centenas de empresas."
                    ctaLabel="Criar anúncio"
                    onCta={openNew}
                    testid="advertising-empty"
                />
            ) : (
                <div className="grid lg:grid-cols-2 gap-4">
                    {ads.map((a) => (
                        <Card key={a.id} className="glass-card p-0 rounded-2xl border-white/10 overflow-hidden" data-testid={`ad-${a.id}`}>
                            {a.image_url && (
                                <div className="aspect-video bg-black/30 overflow-hidden">
                                    <img src={a.image_url} alt="" className="w-full h-full object-cover" />
                                </div>
                            )}
                            <div className="p-4 space-y-3">
                                <div className="flex items-start justify-between gap-3 flex-wrap">
                                    <div className="min-w-0">
                                        <h3 className="font-display font-semibold">{a.title}</h3>
                                        <p className="text-sm text-muted-foreground">{a.headline}</p>
                                    </div>
                                    <StatusBadge status={adStatusKey(a.status)} />
                                </div>
                                {a.industry && (
                                    <Badge variant="outline" className="text-xs">{a.industry}</Badge>
                                )}
                                {a.admin_note && a.status === "rejeitado" && (
                                    <div className="text-xs text-[#FB7185] bg-[#FB7185]/10 border border-[#FB7185]/25 rounded-lg p-2">
                                        <strong>Motivo da rejeição:</strong> {a.admin_note}
                                    </div>
                                )}
                                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                                    <span>{(a.impressions || 0).toLocaleString("pt-PT")} impressões</span>
                                    <span>{(a.clicks || 0).toLocaleString("pt-PT")} cliques</span>
                                    <span>{a.budget_aoa.toLocaleString("pt-PT")} AOA</span>
                                </div>
                                <div className="flex gap-2 pt-2 border-t border-white/5">
                                    <Button size="sm" variant="outline" onClick={() => openEdit(a)} data-testid={`ad-edit-${a.id}`}>
                                        <Edit3 className="h-3.5 w-3.5" />
                                    </Button>
                                    <Button size="sm" variant="outline" onClick={() => remove(a)} data-testid={`ad-delete-${a.id}`}>
                                        <Trash2 className="h-3.5 w-3.5 text-[#FB7185]" />
                                    </Button>
                                    {a.target_url && (
                                        <a href={a.target_url} target="_blank" rel="noopener noreferrer" className="ml-auto">
                                            <Button size="sm" variant="ghost">
                                                <ExternalLink className="h-3.5 w-3.5" />
                                            </Button>
                                        </a>
                                    )}
                                </div>
                            </div>
                        </Card>
                    ))}
                </div>
            )}

            <Dialog open={open} onOpenChange={setOpen}>
                <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
                    <DialogHeader>
                        <DialogTitle>{editing ? "Editar anúncio" : "Novo anúncio"}</DialogTitle>
                        <DialogDescription>O anúncio será enviado para revisão após guardar.</DialogDescription>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <Label>Título principal *</Label>
                            <Input value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })} placeholder="Ex: Contabilidade simples para PMEs" data-testid="ad-title-input" />
                        </div>
                        <div>
                            <Label>Headline curto *</Label>
                            <Input value={form.headline} onChange={(e) => setForm({ ...form, headline: e.target.value })} placeholder="Uma frase de impacto" maxLength={120} data-testid="ad-headline-input" />
                            <p className="text-xs text-muted-foreground mt-1">{form.headline.length}/120</p>
                        </div>
                        <div>
                            <Label>Descrição</Label>
                            <Textarea rows={3} value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} maxLength={400} />
                            <p className="text-xs text-muted-foreground mt-1">{(form.description || "").length}/400</p>
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Setor / Indústria</Label>
                                <Select value={form.industry} onValueChange={(v) => setForm({ ...form, industry: v })}>
                                    <SelectTrigger><SelectValue /></SelectTrigger>
                                    <SelectContent>
                                        {INDUSTRIES.map((i) => <SelectItem key={i} value={i}>{i}</SelectItem>)}
                                    </SelectContent>
                                </Select>
                            </div>
                            <div>
                                <Label>Texto do botão (CTA)</Label>
                                <Input value={form.cta_label} onChange={(e) => setForm({ ...form, cta_label: e.target.value })} maxLength={20} />
                            </div>
                        </div>
                        <div>
                            <Label>Link de destino *</Label>
                            <Input value={form.target_url} onChange={(e) => setForm({ ...form, target_url: e.target.value })} placeholder="https://..." data-testid="ad-targeturl-input" />
                        </div>
                        <div>
                            <Label>Audiência alvo (opcional)</Label>
                            <Input value={form.target_audience} onChange={(e) => setForm({ ...form, target_audience: e.target.value })} placeholder="Ex: PMEs em Luanda, restauração, retalho" />
                        </div>
                        <div>
                            <Label>Imagem do anúncio (16:9)</Label>
                            {form.image_url ? (
                                <div className="relative mt-1">
                                    <img src={form.image_url} alt="" className="w-full aspect-video object-cover rounded-xl border border-white/10" />
                                    <Button size="sm" variant="outline" className="absolute top-2 right-2" onClick={() => setForm({ ...form, image_url: "" })}>
                                        <Trash2 className="h-3.5 w-3.5" />
                                    </Button>
                                </div>
                            ) : (
                                <Button onClick={generateImage} disabled={generating} variant="outline" className="mt-1 w-full gap-2" data-testid="ad-generate-image-button">
                                    {generating ? <Loader2 className="h-4 w-4 animate-spin" /> : <Sparkles className="h-4 w-4" />}
                                    Gerar imagem com IA (16:9)
                                </Button>
                            )}
                        </div>
                        <div className="grid grid-cols-2 gap-3">
                            <div>
                                <Label>Orçamento (AOA)</Label>
                                <Input type="number" value={form.budget_aoa} onChange={(e) => setForm({ ...form, budget_aoa: Number(e.target.value) })} />
                            </div>
                            <div>
                                <Label>Duração (dias)</Label>
                                <Input type="number" value={form.duration_days} onChange={(e) => setForm({ ...form, duration_days: Number(e.target.value) })} />
                            </div>
                        </div>
                    </div>
                    <DialogFooter>
                        <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
                        <Button onClick={save} disabled={saving} className="btn-grad-purple text-white" data-testid="ad-save-button">
                            {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : null}
                            {editing ? "Atualizar e enviar para revisão" : "Submeter para revisão"}
                        </Button>
                    </DialogFooter>
                </DialogContent>
            </Dialog>
        </div>
    );
}
